import { useState } from 'react'
import { Button, Box, Typography, CircularProgress } from '@mui/material'

import { useGetKnowledgeBaseCollectionQuery, useReprocessKnowledgeBaseDocumentsMutation } from '@/core/services/knowledge-base'
import DocumentsTable from '../components/DocumentsTable'
import UploadDocumentModal, { FormValues } from '../components/UploadDocumentModal'
import { Trans } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { getApiErrorMessage } from '@/utils/errors'
import { KnowledgeBaseEmbeddedDocument } from '@/core/models/knowledge-base-collection.model'
import { fileApi } from '@/core/api/file'
import { objectTypes } from '@/core/models/document.model'
import { useAddFileMutation } from '@/core/services/file'

type CollectionDetailsProps = {
  collectionId: string
}

function CollectionDetails({ collectionId }: CollectionDetailsProps) {
  const {
    data: collection,
    isLoading,
    refetch,
  } = useGetKnowledgeBaseCollectionQuery(collectionId, {
    pollingInterval: 5000, // Poll for status updates
  })
  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const [deleteFile, { isLoading: isDeleting }] = fileApi.useDeleteFilesMutation()
  const [reprocessDocument, { isLoading: isReprocessing }] = useReprocessKnowledgeBaseDocumentsMutation()
  const [addFile, { isLoading: isUploading }] = useAddFileMutation()

  if (isLoading) {
    return <CircularProgress />
  }

  if (!collection) {
    return (
      <div>
        <Trans id="admin.knowledge.collectionDetails.notFound">Collection not found</Trans>
      </div>
    )
  }

  const handleUploadComplete = () => {
    setUploadModalOpen(false)
    refetch()
  }

  const handleUpload = async (data: FormValues) => {
    if (!data.file) return

    try {
      await addFile({
        file: data.file,
        objectType: objectTypes.KNOWLEDGE_BASE,
        objectId: collectionId,
      }).unwrap()

      enqueueSnackbar('Document uploaded successfully and is now processing.', { variant: 'success' })
      handleUploadComplete()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to upload document'), { variant: 'error' })
    }
  }

  const handleDelete = async (document: KnowledgeBaseEmbeddedDocument) => {
    if (window.confirm('Are you sure you want to delete this document? This action is irreversible.')) {
      try {
        await deleteFile({
          files: [document],
          objectType: objectTypes.KNOWLEDGE_BASE,
          objectId: collectionId,
        }).unwrap()
        enqueueSnackbar('Document deleted successfully', { variant: 'success' })
      } catch (error) {
        enqueueSnackbar(getApiErrorMessage(error, 'Failed to delete document'), { variant: 'error' })
      }
    }
  }

  const handleReprocess = async (documentKey: string) => {
    try {
      await reprocessDocument({ collectionId, body: { documentKeys: [documentKey] } }).unwrap()
      enqueueSnackbar('Document reprocessing started', { variant: 'info' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to start reprocessing'), { variant: 'error' })
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            {collection.name}
          </Typography>
          <Typography color="text.secondary">{collection.description || ''}</Typography>
        </Box>
        <Button variant="contained" onClick={() => setUploadModalOpen(true)}>
          <Trans>Upload Document</Trans>
        </Button>
      </Box>

      <DocumentsTable
        documents={collection.documents || []}
        isLoading={isLoading}
        isProcessing={isDeleting || isReprocessing}
        onDelete={handleDelete}
        onReprocess={handleReprocess}
      />

      <UploadDocumentModal open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} onUpload={handleUpload} isLoading={isUploading} />
    </>
  )
}

export default CollectionDetails
